import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../hooks/useAuth';
import { getDownloads, type DownloadEntry } from '../api/downloads';

// ---------------------------------------------------------------------------
// Platform download definitions (fallback if API fails)
// ---------------------------------------------------------------------------

const FALLBACK_DOWNLOADS: DownloadEntry[] = [
  { platform: 'macOS', icon: '\u{1F34E}', filename: 'flux-darwin-arm64', arch: 'Apple Silicon', url: '#' },
  { platform: 'macOS', icon: '\u{1F34E}', filename: 'flux-darwin-amd64', arch: 'Intel', url: '#' },
  { platform: 'Linux', icon: '\u{1F427}', filename: 'flux-linux-amd64', arch: 'x86_64', url: '#' },
  { platform: 'Linux', icon: '\u{1F427}', filename: 'flux-linux-arm64', arch: 'ARM64', url: '#' },
  { platform: 'Windows', icon: '\u{1FA9F}', filename: 'flux-windows-amd64.exe', arch: 'x86_64', url: '#' },
  { platform: 'Windows', icon: '\u{1FA9F}', filename: 'flux-windows-arm64.exe', arch: 'ARM64', url: '#' },
];

const MANUAL_INSTALL = `# macOS/Linux
chmod +x flux-*
mkdir -p ~/.local/bin
mv flux-* ~/.local/bin/flux

# Add ~/.local/bin to your PATH (if not already there):
# echo 'export PATH="$HOME/.local/bin:$PATH"' >> ~/.zshrc

# Windows
Rename to flux.exe and add to PATH`;

const CONFIG_BLOCK = `mkdir -p ~/.flux && cat > ~/.flux/config.json << 'EOF'
{
  "cognito_domain": "llmgw-prod-auth.auth.us-east-1.amazoncognito.com",
  "client_id": "138kd1hg20d2noshdtrkc3uij0",
  "gateway_url": "https://flux.apex.ermbro.com",
  "callback_port": "19876"
}
EOF`;

const LOGIN_CMD = 'flux login';

const CLAUDE_CMD = 'flux';

interface AvailableModel {
  alias: string;
  description: string;
}

const AVAILABLE_MODELS: AvailableModel[] = [
  { alias: 'claude-sonnet', description: 'Claude Sonnet 4.6' },
  { alias: 'claude-opus', description: 'Claude Opus 4.8 (default)' },
  { alias: 'claude-fable-5', description: 'Claude Fable 5 (flagship reasoning, opt-in)' },
  { alias: 'claude-opus-4-7', description: 'Claude Opus 4.7' },
  { alias: 'claude-opus-4-6', description: 'Claude Opus 4.6' },
  { alias: 'claude-haiku', description: 'Claude Haiku 4.5' },
];

// ---------------------------------------------------------------------------
// CopyButton component
// ---------------------------------------------------------------------------

function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = useCallback(() => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }, [text]);

  return (
    <button
      type="button"
      className="btn btn--sm btn--secondary"
      onClick={handleCopy}
      style={{
        position: 'absolute',
        top: 8,
        right: 8,
        fontSize: 11,
        padding: '3px 10px',
        zIndex: 2,
      }}
    >
      {copied ? 'Copied!' : 'Copy'}
    </button>
  );
}

// ---------------------------------------------------------------------------
// CodeBlock component
// ---------------------------------------------------------------------------

function CodeBlock({ code, language }: { code: string; language?: string }) {
  return (
    <div style={{ position: 'relative' }}>
      <CopyButton text={code} />
      <pre
        style={{
          background: '#0a1225',
          border: '1px solid var(--border-default)',
          borderRadius: 6,
          padding: '16px 16px 16px 16px',
          overflowX: 'auto',
          fontFamily: "'SFMono-Regular', Consolas, 'Liberation Mono', Menlo, monospace",
          fontSize: 13,
          lineHeight: 1.6,
          color: '#e8edf5',
          margin: 0,
        }}
      >
        <code>{code}</code>
      </pre>
    </div>
  );
}

// ---------------------------------------------------------------------------
// StepCard component
// ---------------------------------------------------------------------------

function StepCard({
  step,
  title,
  children,
}: {
  step: number;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div
      style={{
        background: 'var(--bg-surface)',
        border: '1px solid var(--border-default)',
        borderRadius: 8,
        padding: 24,
        marginBottom: 20,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
        <span
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 28,
            height: 28,
            borderRadius: '50%',
            background: 'var(--accent-blue)',
            color: '#fff',
            fontSize: 13,
            fontWeight: 700,
            flexShrink: 0,
          }}
        >
          {step}
        </span>
        <h3
          style={{
            fontSize: 16,
            fontWeight: 600,
            color: 'var(--text-primary)',
            margin: 0,
          }}
        >
          {title}
        </h3>
      </div>
      {children}
    </div>
  );
}

// ---------------------------------------------------------------------------
// SetupView component
// ---------------------------------------------------------------------------

export function SetupView() {
  const { getJwtToken } = useAuth();
  const [manualOpen, setManualOpen] = useState(false);
  const [downloads, setDownloads] = useState<DownloadEntry[]>(FALLBACK_DOWNLOADS);

  useEffect(() => {
    (async () => {
      try {
        const token = await getJwtToken();
        if (token) {
          const data = await getDownloads(token);
          if (data.length > 0) setDownloads(data);
        }
      } catch {
        // Fall back to static links
      }
    })();
  }, [getJwtToken]);

  return (
    <div>
      {/* Header */}
      <div className="view__header">
        <h1 className="view__title">Get Started with Flux</h1>
      </div>

      <p
        style={{
          color: 'var(--text-secondary)',
          fontSize: 14,
          lineHeight: 1.6,
          marginBottom: 24,
          maxWidth: 640,
        }}
      >
        Follow these steps to install the Flux CLI, configure your environment,
        and start using Claude Code through the Flux gateway.
      </p>

      {/* Step 1: Download the CLI */}
      <StepCard step={1} title="Download the CLI">
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
            gap: 10,
            marginBottom: 16,
          }}
        >
          {downloads.map((dl) => (
            <a
              key={dl.filename}
              href={dl.url}
              download
              className="btn btn--secondary"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                padding: '12px 16px',
                textDecoration: 'none',
                textAlign: 'left',
                fontSize: 13,
              }}
            >
              <span style={{ fontSize: 20 }}>{dl.icon}</span>
              <span>
                <span style={{ display: 'block', fontWeight: 600, color: 'var(--text-primary)' }}>
                  {dl.platform} {dl.arch}
                </span>
                <span
                  style={{
                    display: 'block',
                    fontSize: 11,
                    color: 'var(--text-muted)',
                    fontFamily: 'monospace',
                    marginTop: 2,
                  }}
                >
                  {dl.filename}
                </span>
              </span>
            </a>
          ))}
        </div>

        {/* Manual install collapsible */}
        <div>
          <button
            type="button"
            onClick={() => setManualOpen(!manualOpen)}
            style={{
              background: 'none',
              border: 'none',
              color: 'var(--accent-cyan)',
              cursor: 'pointer',
              fontSize: 13,
              padding: 0,
              display: 'flex',
              alignItems: 'center',
              gap: 6,
            }}
          >
            <span
              style={{
                display: 'inline-block',
                transform: manualOpen ? 'rotate(90deg)' : 'rotate(0deg)',
                transition: 'transform 0.15s',
                fontSize: 10,
              }}
            >
              &#x25B6;
            </span>
            Manual install instructions
          </button>
          {manualOpen && (
            <div style={{ marginTop: 10 }}>
              <CodeBlock code={MANUAL_INSTALL} language="bash" />
            </div>
          )}
        </div>
      </StepCard>

      {/* Step 2: Configure */}
      <StepCard step={2} title="Configure">
        <p
          style={{
            color: 'var(--text-secondary)',
            fontSize: 13,
            marginBottom: 12,
            lineHeight: 1.5,
          }}
        >
          Create the Flux configuration file with your gateway settings:
        </p>
        <CodeBlock code={CONFIG_BLOCK} language="bash" />
      </StepCard>

      {/* Step 3: Login */}
      <StepCard step={3} title="Login">
        <CodeBlock code={LOGIN_CMD} language="bash" />
        <p
          style={{
            color: 'var(--text-secondary)',
            fontSize: 13,
            marginTop: 12,
            lineHeight: 1.5,
          }}
        >
          This opens your browser to authenticate. After login, your credentials
          are stored locally and refresh automatically.
        </p>
      </StepCard>

      {/* Step 4: Use with Claude Code */}
      <StepCard step={4} title="Use with Claude Code">
        <CodeBlock code={CLAUDE_CMD} language="bash" />
        <p
          style={{
            color: 'var(--text-secondary)',
            fontSize: 13,
            marginTop: 12,
            lineHeight: 1.5,
          }}
        >
          That's it! Claude Code automatically uses your Flux credentials.
          No need to export API keys.
        </p>
      </StepCard>

      {/* Available Models */}
      <section className="view__section" style={{ marginTop: 8 }}>
        <h2 className="view__section-title">Available Models</h2>
        <div className="settings-card">
          {AVAILABLE_MODELS.map((m) => (
            <div key={m.alias} className="settings-card__row">
              <span
                className="settings-card__label"
                style={{
                  fontFamily: 'monospace',
                  color: 'var(--accent-cyan)',
                  minWidth: 140,
                }}
              >
                {m.alias}
              </span>
              <span className="settings-card__value">{m.description}</span>
            </div>
          ))}
        </div>
      </section>

      {/* Need Help */}
      <section className="view__section" style={{ marginTop: 8 }}>
        <h2 className="view__section-title">Need Help?</h2>
        <div
          style={{
            background: 'var(--bg-surface)',
            border: '1px solid var(--border-default)',
            borderRadius: 8,
            padding: 20,
            fontSize: 14,
            color: 'var(--text-secondary)',
            lineHeight: 1.6,
          }}
        >
          Having trouble getting set up? Contact your Flux administrator for
          assistance.
        </div>
      </section>
    </div>
  );
}
